import React, { useMemo } from 'react';

import { divide, get } from 'lodash';
import Form from 'mobx-react-form';
import { observer } from 'mobx-react-lite';
import { Grid, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { RentRollTableColumn } from 'types/underwriting/income-approach';
import RentRollTable from '../RentRollTable';
import { FormOptions, wrapForm } from 'components/shared/form';
import NumericInput from 'components/shared/inputs/NumericInput';

import { formOptions } from 'forms/utils/formOptions';
import { plugins } from 'forms/utils/formPlugins';
import { RENT_ROLL_LABELS } from '../../constants';
import { useRootStore } from 'stores/RootStore';
import { rentRollFormFields, rentRollFormHelpers } from './rentRollFormConfig';
import { groupCompsByBedrooms } from '../rent-comps/ResidentialRentComps';
import { getSubjectUnitGroups } from '../helpers';
import { SummaryTable } from './SummaryTable';
import { formatCurrencyFloat, formatPercentageString } from 'shared/utils/numberFormatters';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { INCOME_APPROACH_BASIS_TYPES } from 'shared/calculations/incomeApproach';

const formConfig: FormOptions = {
  fields: rentRollFormFields,
  options: {
    ...formOptions,
    validateOnChange: true,
  },
  plugins,
};

type ResidentialRentRollProps = {
  form: Form;
};

const ProjectedResidentialRentRoll = observer(({ form }: ResidentialRentRollProps) => {
  const { underwritingPageStore } = useRootStore();
  const { underwritingData = {} as UnderwritingOutput } = underwritingPageStore;
  const { property } = underwritingData || {};
  const isProjectedRentRoll = true;
  const columns: RentRollTableColumn[] = rentRollFormHelpers.getUnitsTableColumns(isProjectedRentRoll);

  const rentComps = get(underwritingData, 'incomeApproach.residential.rentComps.comps', []);

  const rentRollField = form.$('incomeApproach.residential.rentRoll');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rentRoll = rentRollField.value || [];

  const residentialVCLossPercentageField = form.$('incomeApproach.residential.vcLossPercentage');
  const residentialVCLossPercentage = residentialVCLossPercentageField.value;

  const borrowerTotal = rentRollFormHelpers.getResidentialIncomeTotal(form, INCOME_APPROACH_BASIS_TYPES.borrower);
  const borrowerLoss = useMemo(() => {
    return divide(borrowerTotal * residentialVCLossPercentage, 100);
  }, [borrowerTotal, residentialVCLossPercentage]);
  const effectiveBorrowerIncome = borrowerTotal - borrowerLoss;

  const underwriterTotal = useMemo(() => {
    return rentRollFormHelpers.getResidentialIncomeTotal(form, INCOME_APPROACH_BASIS_TYPES.projected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentRoll]);
  const underwriterLoss = useMemo(() => {
    return divide(underwriterTotal * residentialVCLossPercentage, 100);
  }, [underwriterTotal, residentialVCLossPercentage]);
  const effectiveUnderwriterIncome = underwriterTotal - underwriterLoss;

  const compGroups = groupCompsByBedrooms(rentComps);
  const subjectUnitGroups = useMemo(() => {
    return getSubjectUnitGroups(rentRoll);
  }, [rentRoll]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sx={{ marginTop: '16px', marginBottom: '16px' }}>
        <Grid item xs={12} sx={{ pb: 1 }}>
          <Typography variant="h6">Borrower & Comparables Summary</Typography>
        </Grid>
        <Grid item xs={12}>
          {compGroups.map((compGroup) => {
            return <SummaryTable compGroup={compGroup} subjectUnitGroups={subjectUnitGroups} />;
          })}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">Projected Rent Roll</Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <NumericInput
              disabled
              size="small"
              value={property?.residentialUnits ?? 0}
              label={RENT_ROLL_LABELS.NUMBER_OF_RESIDENTIAL_UNITS}
            />
          </Grid>
          <Grid item xs={12}>
            <RentRollTable form={form} columns={columns} isProjectedRentRoll={isProjectedRentRoll} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ pt: 2 }}>
        <Typography variant="h6">Vacancy & Collection Loss</Typography>
        <Grid item xs={2}>
          <NumericInput
            {...residentialVCLossPercentageField.bind()}
            size="small"
            margin="normal"
            fullWidth
            label="Residential VC Loss %"
            decimalScale={2}
            max={100}
            allowNegative={false}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
        <Grid xs={6}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell align="right">Borrower</TableCell>
                <TableCell align="right">Projected</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ pl: 2 }}>Potential Residential Income</TableCell>
                <TableCell align="right">{formatCurrencyFloat(borrowerTotal)}</TableCell>
                <TableCell align="right">{formatCurrencyFloat(underwriterTotal)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 2 }}>
                  Less Residential V/C Loss @ {formatPercentageString(residentialVCLossPercentage)}
                </TableCell>
                <TableCell align="right">{formatCurrencyFloat(-1 * borrowerLoss)}</TableCell>
                <TableCell align="right">{formatCurrencyFloat(-1 * underwriterLoss)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Effective Residential Income</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">
                  {formatCurrencyFloat(effectiveBorrowerIncome)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">
                  {formatCurrencyFloat(effectiveUnderwriterIncome)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Grid>
  );
});

export default wrapForm({
  ...formConfig,
})(ProjectedResidentialRentRoll);
